import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashboardShell from '../../../layouts/DashboardShell';
import { clientSideLinks } from '../clientRoutes';
import { fetchExpiredJobs } from '../../../services/api';
import CardIcon from '../../../shared/components/icons/CardIcon';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
};

function ExpiredJobDetailsPage() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadJob = async () => {
      try {
        setLoading(true);
        const jobs = await fetchExpiredJobs();
        const selected = (Array.isArray(jobs) ? jobs : jobs?.data || []).find((item) => item.id === id);
        setJob(selected || null);
      } catch (err) {
        setError(err.message || 'Unable to load this expired job.');
      } finally {
        setLoading(false);
      }
    };

    loadJob();
  }, [id]);

  if (loading) {
    return (
      <DashboardShell role="CLIENT" sideLinks={clientSideLinks}>
        <div className="rounded-3xl bg-white p-8 text-center text-slate-600 shadow-sm">Loading expired job...</div>
      </DashboardShell>
    );
  }

  if (error || !job) {
    return (
      <DashboardShell role="CLIENT" sideLinks={clientSideLinks}>
        <div className="rounded-3xl bg-rose-50 p-8 text-center text-rose-700 shadow-sm">
          {error || 'Expired job not found.'}
        </div>
      </DashboardShell>
    );
  }

  return (
    <DashboardShell role="CLIENT" sideLinks={clientSideLinks}>
      <div className="space-y-6">
        <Link to="/client/expired-jobs" className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
          <CardIcon name="arrowLeft" />Back to Expired Jobs
        </Link>

        <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] text-rose-700">
              <CardIcon name="warning" />EXPIRED
            </span>
          </div>

          <h1 className="mt-5 text-3xl font-bold text-navy">{job.title}</h1>
          <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700"><CardIcon name="wrench" />{job.skill?.name || 'General work'}</p>
          <p className="mt-1 text-sm text-slate-600">{job.skill?.category?.name || 'Uncategorized'}</p>

          <div className="mt-6 space-y-3 text-sm text-slate-700">
            <p className="inline-flex items-center gap-1.5"><CardIcon name="location" />{[job.area, job.city].filter(Boolean).join(', ') || 'Location unavailable'}</p>
            <p className="mt-3 text-base font-semibold text-navy">{job.description || 'No description provided.'}</p>
            <div className="grid gap-3 border-t border-slate-100 pt-4 sm:grid-cols-2">
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-slate-500"><CardIcon name="money" />Budget</p>
                <p className="mt-2 text-xl font-bold text-orange-600">K{job.budget}</p>
                <p className="mt-1 text-sm text-slate-600">{job.isNegotiable ? 'Negotiable' : 'Fixed price'}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-slate-500"><CardIcon name="people" />People Required</p>
                <p className="mt-2 text-xl font-bold text-navy">{job.requiredPeople ?? 1}</p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 p-4">
                <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-slate-500"><CardIcon name="clock" />Work Type</p>
                <p className="mt-2 font-semibold text-slate-900">{(job.workType || 'PART_TIME').replace('_', ' ')}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 p-4">
                <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-slate-500"><CardIcon name="flame" />Urgency</p>
                <p className="mt-2 font-semibold text-slate-900">{String(job.urgency || 'normal').toUpperCase()}</p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 p-4">
                <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-slate-500"><CardIcon name="calendar" />Posted</p>
                <p className="mt-2 font-semibold text-slate-900">{formatDate(job.createdAt)}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 p-4">
                <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-slate-500"><CardIcon name="calendar" />Expired</p>
                <p className="mt-2 font-semibold text-slate-900">{formatDate(job.expiresAt)}</p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-slate-500"><CardIcon name="eye" />Applications</p>
              <p className="mt-2 text-lg font-bold text-navy">{job.applicationsCount ?? 0}</p>
            </div>
          </div>
        </article>
      </div>
    </DashboardShell>
  );
}

export default ExpiredJobDetailsPage;
